import { Component, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Grain from "@/components/Grain";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Rubriche from "@/components/Rubriche";
import Editoria from "@/components/Editoria";
import Manifesto from "@/components/Manifesto";
import HomeShopModules from "@/components/shop/HomeShopModules";
import Newsletter from "@/components/Newsletter";
import Footer from "@/components/Footer";
import ShopLanding from "@/pages/ShopLanding";
import TackleList from "@/pages/TackleList";
import ProductPage from "@/pages/ProductPage";
import ArchiveList from "@/pages/ArchiveList";
import ArchiveProduct from "@/pages/ArchiveProduct";
import EditionsList from "@/pages/EditionsList";
import EditionProduct from "@/pages/EditionProduct";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import AdminApp from "@/pages/AdminApp";
import { CartProvider } from "@/shop/CartContext";
import { scrollToId } from "@/lib/scroll";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-ink text-bone flex items-center justify-center font-serif">
          <p className="italic text-xl">L'acqua è torbida, torniamo subito.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function EditoriaWithFilter() {
  const [filter, setFilter] = useState("tutte");
  useEffect(() => {
    const handler = (e) => setFilter(e.detail);
    window.addEventListener("rubric-select", handler);
    return () => window.removeEventListener("rubric-select", handler);
  }, []);
  return <Editoria filter={filter} onFilterChange={setFilter} />;
}

const Home = () => {
  const onSelectRubric = (id) => {
    window.dispatchEvent(new CustomEvent("rubric-select", { detail: id }));
    scrollToId("numeri");
  };

  return (
    <div className="relative min-h-screen bg-ink text-bone">
      <Grain />
      <Nav />
      <main>
        <Hero />
        <Marquee />
        <Rubriche onSelect={onSelectRubric} />
        <EditoriaWithFilter />
        <Manifesto />
        <HomeShopModules />
        <Newsletter />
      </main>
      <Footer />
    </div>
  );
};

const ShopShell = ({ children }) => (
  <div className="relative min-h-screen bg-ink text-bone">
    <Grain />
    <Nav />
    <main>{children}</main>
    <Footer />
  </div>
);

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <CartProvider>
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: "#131D18",
                border: "1px solid #212F28",
                color: "#ECE7DE",
                fontFamily: "Fraunces, serif",
              },
            }}
          />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<ShopShell><ShopLanding /></ShopShell>} />
            <Route path="/shop/selected-tackle" element={<ShopShell><TackleList /></ShopShell>} />
            <Route path="/shop/selected-tackle/:brand/:slug" element={<ShopShell><ProductPage /></ShopShell>} />
            <Route path="/shop/selected-tackle/:slug" element={<ShopShell><ProductPage /></ShopShell>} />
            <Route path="/shop/archive" element={<ShopShell><ArchiveList /></ShopShell>} />
            <Route path="/shop/archive/:slug" element={<ShopShell><ArchiveProduct /></ShopShell>} />
            <Route path="/shop/editions" element={<ShopShell><EditionsList /></ShopShell>} />
            <Route path="/shop/editions/:slug" element={<ShopShell><EditionProduct /></ShopShell>} />
            <Route path="/cart" element={<ShopShell><CartPage /></ShopShell>} />
            <Route path="/checkout" element={<ShopShell><CheckoutPage /></ShopShell>} />
            <Route path="/checkout/success" element={<ShopShell><CheckoutSuccess /></ShopShell>} />
            <Route path="/admin" element={<AdminApp />} />
          </Routes>
        </CartProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
